/**
 * Per-course chrome: the course title bar and the tab strip beneath it.
 *
 * Classroom shows Stream / Classwork / People / Grades as tabs inside a course.
 * C-GC keeps those four in the same place and order, then adds the Canvas
 * concepts Classroom has no page for — Modules, Files, Discussions, Syllabus —
 * plus the grade calculator, which is a C-GC original.
 *
 * That split is brainstorm.md decision 1.9: 1:1 on design language, superset on
 * page inventory. Hiding Modules to preserve an aesthetic rule would cripple
 * the app, because Modules are how many Canvas courses are actually organized.
 */
import { Link as RouterLink, Outlet, useLocation, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { useCourse } from '@/lib/canvas/queries';
import { tokens } from '@/theme/tokens';

import { COURSE_TABS, activeSegment } from './courseTabs';

export function CourseLayout() {
  const { courseId = '' } = useParams();
  const location = useLocation();
  const { data: course, isPending } = useCourse(courseId);

  const current = activeSegment(location.pathname, courseId);

  return (
    <Box>
      <Box
        sx={{
          borderBottom: `${tokens.size.borderWidth}px solid`,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: { xs: 2, sm: 3 },
          pt: 2,
        }}
      >
        <Box sx={{ maxWidth: tokens.size.contentMaxWidth, mx: 'auto', width: '100%' }}>
          {isPending ? (
            <Skeleton width="40%" height={32} />
          ) : (
            <>
              <Typography variant="h5" component="h1" sx={{ wordBreak: 'break-word' }}>
                {course?.name ?? 'Course'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[course?.course_code, course?.term?.name].filter(Boolean).join(' · ')}
                {course?.concluded ? ' · Concluded' : ''}
              </Typography>
            </>
          )}

          <Tabs
            value={current}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mt: 1 }}
            aria-label="Course sections"
          >
            {COURSE_TABS.map((tab) => (
              <Tab
                key={tab.segment}
                value={tab.segment}
                label={tab.label}
                component={RouterLink}
                to={tab.segment ? `/c/${courseId}/${tab.segment}` : `/c/${courseId}`}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <Outlet />
    </Box>
  );
}
